import React, { useEffect, useRef, useCallback } from "react";
import { Divider, Typography, Card, Col, Row, Button } from "antd";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ACTIONS from "../../store/action";
import { ReactComponent as AIChatBotIcon } from "../../assets/svg/icon-ai-chat-bot.svg";

import {
  SuggestedQuestionsCardStyle,
  SuggestedQuestionsCardBodyStyle,
  SuggestedQuestionsCardIconStyle,
  SuggestedQuestionsCardTitleStyle,
  SuggestedQuestionsCardThemeStyle,
  SuggestedQuestionsCardTopSectionStyle,
  SuggestedQuestionsCardBottomSectionStyle,
  SuggestedQuestionsCardTitleContainerStyle,
  SuggestedQuestionsCardDividerStyle,
  SuggestedQuestionsCardQuestionsContainerStyle,
  SuggestedQuestionsCardAskNewQuestionStyle,
  SuggestedQuestionsCardAskNewQuestionContainerStyle,
  SuggestedQuestionsCardQuestionStyle
} from "./Styles";
import { PENDO_EVENTS } from "./Constants";
import { useDashboardBucketsTrackingEvent } from "../../utils/pendo/hooks";

const { Text } = Typography;
const BUTTON_NAME = "custom-button";

const SuggestedQuestionsCard = ({ title, theme, questions, onSuggestedQuestionClick }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { trackEvent } = useDashboardBucketsTrackingEvent();
  const askNewQuestionRef = useRef(null);

  const handleQuestionClick = useCallback(
    (questionText) => {
      if (onSuggestedQuestionClick) {
        onSuggestedQuestionClick(questionText);
      }
    },
    [onSuggestedQuestionClick]
  );

  const handleAskNewQuestion = useCallback(() => {
    trackEvent(PENDO_EVENTS.INTRO_PANEL_NEWQ);
    dispatch(ACTIONS.setAiChatBotIntroPanelShown(false));
  }, [dispatch]);

  // Since we're inside Shadow DOM, we need to use native event listeners
  useEffect(() => {
    const element = askNewQuestionRef.current;
    if (!element) return;

    const clickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleAskNewQuestion();
    };

    element.addEventListener("click", clickHandler, true);
    element.addEventListener("mousedown", clickHandler, true);

    return () => {
      element.removeEventListener("click", clickHandler, true);
      element.removeEventListener("mousedown", clickHandler, true);
    };
  }, [handleAskNewQuestion]);

  const attachQuestionListeners = useCallback(
    (element, questionText) => {
      if (!element) return;

      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleQuestionClick(questionText);
      };

      element.addEventListener("click", clickHandler, true);

      return () => {
        element.removeEventListener("click", clickHandler, true);
      };
    },
    [handleQuestionClick]
  );

  return (
    <Card bordered={false} style={SuggestedQuestionsCardStyle} bodyStyle={SuggestedQuestionsCardBodyStyle}>
      <Col style={SuggestedQuestionsCardTopSectionStyle}>
        <AIChatBotIcon style={SuggestedQuestionsCardIconStyle} />
        <Col style={SuggestedQuestionsCardTitleContainerStyle}>
          <Text style={SuggestedQuestionsCardTitleStyle}>{title}</Text>
          <Text style={SuggestedQuestionsCardThemeStyle}>
            {t("ai_chat_bot.intro_panel.suggested_questions_card.ask_questions_about", { theme })}
          </Text>
          <Divider style={SuggestedQuestionsCardDividerStyle} />
        </Col>
      </Col>

      <Col style={SuggestedQuestionsCardBottomSectionStyle}>
        <Col style={SuggestedQuestionsCardQuestionsContainerStyle}>
          {questions.map((question) => (
            <Button
              type="text"
              className={BUTTON_NAME}
              key={question?.replace(" ", "_")}
              style={SuggestedQuestionsCardQuestionStyle}
              ref={(el) => {
                if (el) {
                  const cleanup = attachQuestionListeners(el, question);
                  el._cleanup = cleanup;
                }
              }}
            >
              {question}
            </Button>
          ))}
        </Col>

        <Row style={{ display: "flex", justifyContent: "center", cursor: "pointer" }} ref={askNewQuestionRef}>
          <Row style={SuggestedQuestionsCardAskNewQuestionContainerStyle}>
            <Text style={SuggestedQuestionsCardAskNewQuestionStyle}>
              {t("ai_chat_bot.intro_panel.suggested_questions_card.ask_a_new_question")}
            </Text>
          </Row>
        </Row>
      </Col>
    </Card>
  );
};

export { SuggestedQuestionsCard };
